import { useQuery } from "convex/react";
import {
	BookOpenIcon,
	CreditCardIcon,
	FlaskConicalIcon,
	InboxIcon,
	KeyRoundIcon,
	LinkIcon,
	LogOutIcon,
	MailIcon,
	MoonIcon,
	ScaleIcon,
	Settings,
	ShieldIcon,
	SunIcon,
	UsersIcon,
} from "lucide-react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import LogoIcon from "@/components/LogoIcon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarRail,
} from "@/components/ui/sidebar";
import { useDarkMode } from "@/hooks/use-dark-mode";
import { authClient } from "@/lib/authClient";
import { cn } from "@/lib/utils";
import { api } from "../../../convex/_generated/api";
import { ImpersonationSidebarControl } from "./ImpersonationControl";
import OrgSwitcher from "./OrgSwitcher";

function useBasePath() {
	const { pathname } = useLocation();
	return pathname.startsWith("/dev/workspace")
		? "/dev/workspace"
		: "/workspace";
}

function getInitials(value: string) {
	return value
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? "")
		.join("");
}

export default function WorkspaceSidebar() {
	const location = useLocation();
	const navigate = useNavigate();
	const base = useBasePath();
	const { isDark, toggle: toggleDarkMode } = useDarkMode();
	const session = authClient.useSession();
	const user = session.data?.user as
		| { name?: string; email?: string; image?: string }
		| undefined;
	const isStaff = useQuery(api.staff.isCurrentUserStaff) === true;
	const isFirmAdmin = useQuery(api.members.myRole) === "admin";

	const navItems = [
		{
			label: "Connections",
			to: `${base}/connections`,
			icon: LinkIcon,
		},
		{
			label: "Members",
			to: `${base}/members`,
			icon: UsersIcon,
		},
		// Billing is admin-only, per the usual SaaS convention.
		...(isFirmAdmin
			? [
					{
						label: "Billing",
						to: `${base}/billing`,
						icon: CreditCardIcon,
					},
				]
			: []),
		{
			label: "Settings",
			to: `${base}/settings`,
			icon: Settings,
		},
		...(isStaff
			? [
					{
						label: "Codex",
						to: `${base}/codex`,
						icon: BookOpenIcon,
					},
				]
			: []),
	];

	const userName = user?.name?.trim() || user?.email || "Clerical user";
	const userEmail = user?.email || "Workspace";

	function handleSignOut() {
		// Fire signOut but don't await — immediately navigate so React
		// never re-renders with a cleared session (which would flash /login).
		void authClient.signOut();
		navigate("/", { replace: true });
	}

	return (
		<Sidebar collapsible="icon">
			<SidebarHeader>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton
							size="lg"
							render={
								<Link to={isStaff ? `${base}/codex` : `${base}/connections`} />
							}
							tooltip="Clerical"
						>
							<div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
								<LogoIcon className="size-5" />
							</div>
							<span className="truncate font-semibold">Clerical</span>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
				<div className="px-2 pb-1 group-data-[collapsible=icon]:hidden">
					<OrgSwitcher />
				</div>
				{isStaff ? (
					<div className="px-2 pb-1 group-data-[collapsible=icon]:hidden">
						<ImpersonationSidebarControl />
					</div>
				) : null}
			</SidebarHeader>
			<SidebarContent>
				<SidebarGroup>
					{isStaff && <SidebarGroupLabel>Workspace</SidebarGroupLabel>}
					<SidebarGroupContent>
						<SidebarMenu>
							{navItems.map((item) => (
								<SidebarMenuItem key={item.to}>
									<SidebarMenuButton
										render={<NavLink to={item.to} />}
										tooltip={item.label}
										isActive={location.pathname.startsWith(item.to)}
									>
										<item.icon />
										<span>{item.label}</span>
									</SidebarMenuButton>
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
				{isStaff ? (
					<SidebarGroup>
						<SidebarGroupLabel>Staff admin</SidebarGroupLabel>
						<SidebarGroupContent>
							<SidebarMenu>
								<SidebarMenuItem>
									<SidebarMenuButton
										render={<NavLink to="/staff/firms" />}
										tooltip="Manage firms"
										isActive={location.pathname.startsWith("/staff/firms")}
									>
										<ShieldIcon />
										<span>Manage firms</span>
									</SidebarMenuButton>
								</SidebarMenuItem>
								<SidebarMenuItem>
									<SidebarMenuButton
										render={<NavLink to="/staff/review" />}
										tooltip="Review"
										isActive={location.pathname.startsWith("/staff/review")}
									>
										<InboxIcon />
										<span>Review</span>
									</SidebarMenuButton>
								</SidebarMenuItem>
								<SidebarMenuItem>
									<SidebarMenuButton
										render={<NavLink to="/staff/scenarios" />}
										tooltip="Scenarios"
										isActive={location.pathname.startsWith("/staff/scenarios")}
									>
										<FlaskConicalIcon />
										<span>Scenarios</span>
									</SidebarMenuButton>
								</SidebarMenuItem>
								<SidebarMenuItem>
									<SidebarMenuButton
										render={<NavLink to="/staff/skills" />}
										tooltip="Skills"
										isActive={location.pathname.startsWith("/staff/skills")}
									>
										<BookOpenIcon />
										<span>Skills</span>
									</SidebarMenuButton>
								</SidebarMenuItem>
								<SidebarMenuItem>
									<SidebarMenuButton
										render={<NavLink to="/staff/subscription-accounts" />}
										tooltip="LLM subscriptions"
										isActive={location.pathname.startsWith(
											"/staff/subscription-accounts",
										)}
									>
										<KeyRoundIcon />
										<span>LLM subscriptions</span>
									</SidebarMenuButton>
								</SidebarMenuItem>
								<SidebarMenuItem>
									<SidebarMenuButton
										render={<NavLink to="/staff/mail" />}
										tooltip="Mail"
										isActive={location.pathname.startsWith("/staff/mail")}
									>
										<MailIcon />
										<span>Mail</span>
									</SidebarMenuButton>
								</SidebarMenuItem>
								<SidebarMenuItem>
									<SidebarMenuButton
										render={<NavLink to="/staff/clio" />}
										tooltip="Clio"
										isActive={location.pathname.startsWith("/staff/clio")}
									>
										<ScaleIcon />
										<span>Clio</span>
									</SidebarMenuButton>
								</SidebarMenuItem>
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
				) : null}
			</SidebarContent>
			<SidebarFooter>
				<div
					className={cn(
						"flex items-center gap-2 rounded-lg border border-sidebar-border/70 bg-sidebar-accent/30 p-2",
						"group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:border-transparent group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:p-0",
					)}
				>
					<Link
						to={`${base}/settings`}
						title="Settings"
						className={cn(
							"flex min-w-0 flex-1 items-center gap-2 rounded-md outline-none hover:bg-sidebar-accent/40 focus-visible:ring-2 focus-visible:ring-ring",
							"group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:hover:bg-transparent",
						)}
					>
						<Avatar>
							<AvatarImage src={user?.image ?? ""} alt={userName} />
							<AvatarFallback>{getInitials(userName)}</AvatarFallback>
						</Avatar>
						<div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
							<span className="truncate font-medium">{userName}</span>
							<span className="truncate text-xs text-sidebar-foreground/70">
								{userEmail}
							</span>
						</div>
					</Link>
					<Button
						variant="ghost"
						size="icon"
						onClick={toggleDarkMode}
						title={isDark ? "Switch to light mode" : "Switch to dark mode"}
						className="shrink-0 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:hidden"
					>
						{isDark ? (
							<SunIcon className="size-4" />
						) : (
							<MoonIcon className="size-4" />
						)}
					</Button>
					<Button
						variant="ghost"
						size="icon"
						onClick={handleSignOut}
						title="Sign out"
						className="shrink-0 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:hidden"
					>
						<LogOutIcon className="size-4" />
					</Button>
				</div>
			</SidebarFooter>
			<SidebarRail />
		</Sidebar>
	);
}
